import {ActionList, ActionMenu, IconButton, VisuallyHidden} from '@primer/react'
import {AlertIcon, CheckIcon, CopyIcon, KebabHorizontalIcon, StackSlashIcon} from '@primer/octicons-react'
import type {RefObject} from 'react'
import type {CopyStackLinksStatus} from '../hooks/use-copy-stack-links'

type CopyMenuItemLabels = {
  copy: string
  copied: string
  copiedAccessible: string
  failedAccessible: string
}

const pullRequestLinksLabels: CopyMenuItemLabels = {
  copy: 'Copy pull request links',
  copied: 'Links copied!',
  copiedAccessible: 'Pull request links copied',
  failedAccessible: 'Failed to copy pull request links',
}

const stackLinkLabels: CopyMenuItemLabels = {
  copy: 'Copy stack link',
  copied: 'Link copied!',
  copiedAccessible: 'Stack link copied',
  failedAccessible: 'Failed to copy stack link',
}

type StackActionsMenuProps = {
  anchorRef: RefObject<HTMLButtonElement | null>
  anchorVariant?: 'default' | 'invisible'
  pullRequestLinksCopyStatus?: CopyStackLinksStatus
  onCopyPullRequestLinks?: () => void
  stackLinkCopyStatus?: CopyStackLinksStatus
  onCopyStackLink?: () => void
  /** Called when pointer or keyboard interaction indicates that the menu may open. */
  onMenuTriggerIntent?: () => void
  /** Called each time the menu opens, however it was opened. */
  onMenuOpen?: () => void
  onMenuToggle?: () => void
  onUnstack: () => void
  unstackInactiveText?: string
}

function CopyMenuItem({
  labels,
  status,
  onCopy,
  placeholderIcon = false,
}: {
  labels: CopyMenuItemLabels
  status?: CopyStackLinksStatus
  onCopy: () => void
  placeholderIcon?: boolean
}) {
  const content =
    status === 'success'
      ? {Icon: CheckIcon, label: labels.copied}
      : status === 'error'
        ? {Icon: AlertIcon, label: 'Failed to copy'}
        : {Icon: CopyIcon, label: labels.copy}
  const accessibleLabel =
    status === 'success' ? labels.copiedAccessible : status === 'error' ? labels.failedAccessible : undefined

  return (
    <ActionList.Item
      onSelect={event => {
        event.preventDefault()
        onCopy()
      }}
    >
      <ActionList.LeadingVisual>
        {placeholderIcon && status !== 'success' && status !== 'error' ? (
          <svg width="16" height="16" aria-hidden="true" focusable="false" />
        ) : (
          <content.Icon />
        )}
      </ActionList.LeadingVisual>
      <span aria-hidden={accessibleLabel ? 'true' : undefined}>{content.label}</span>
      {accessibleLabel && <VisuallyHidden>{accessibleLabel}</VisuallyHidden>}
    </ActionList.Item>
  )
}

export function StackActionsMenu({
  anchorRef,
  anchorVariant = 'invisible',
  pullRequestLinksCopyStatus,
  onCopyPullRequestLinks,
  stackLinkCopyStatus,
  onCopyStackLink,
  onMenuTriggerIntent,
  onMenuOpen,
  onMenuToggle,
  onUnstack,
  unstackInactiveText,
}: StackActionsMenuProps) {
  return (
    <ActionMenu
      anchorRef={anchorRef}
      onOpenChange={isOpen => {
        if (!isOpen) return
        onMenuTriggerIntent?.()
        onMenuOpen?.()
      }}
    >
      <ActionMenu.Anchor>
        <IconButton
          ref={anchorRef}
          icon={KebabHorizontalIcon}
          variant={anchorVariant}
          aria-label="More options"
          onClick={onMenuToggle}
          onPointerEnter={onMenuTriggerIntent}
          onFocus={onMenuTriggerIntent}
        />
      </ActionMenu.Anchor>
      <ActionMenu.Overlay align="end" width="small">
        <ActionList>
          {onCopyStackLink && (
            <CopyMenuItem labels={stackLinkLabels} status={stackLinkCopyStatus} onCopy={onCopyStackLink} />
          )}
          {onCopyPullRequestLinks && (
            <CopyMenuItem
              labels={pullRequestLinksLabels}
              status={pullRequestLinksCopyStatus}
              onCopy={onCopyPullRequestLinks}
              placeholderIcon
            />
          )}
          {(onCopyPullRequestLinks || onCopyStackLink) && <ActionList.Divider />}
          <ActionList.Item variant="danger" onSelect={onUnstack} inactiveText={unstackInactiveText}>
            <ActionList.LeadingVisual>
              <StackSlashIcon />
            </ActionList.LeadingVisual>
            Unstack pull requests
          </ActionList.Item>
        </ActionList>
      </ActionMenu.Overlay>
    </ActionMenu>
  )
}
